import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Tooltip
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CreditCard as CreditCardIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

// Interface for pricing feature
interface PricingFeature {
  name: string;
  free: boolean;
  basic: boolean;
  premium: boolean;
  tooltip?: string;
}

// Interface for pricing plan
interface PricingPlan {
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  popular?: boolean;
  buttonText: string;
  buttonVariant: 'outlined' | 'contained';
  buttonColor: 'primary' | 'secondary' | 'inherit';
}

const PricingPage: React.FC = () => {
  // Auth context
  const { isAuthenticated, userSubscription } = useAuth();
  
  // Navigation
  const navigate = useNavigate();
  
  // State for billing cycle
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  
  // Handle billing cycle change
  const handleBillingCycleChange = (_event: React.MouseEvent<HTMLElement>, newBillingCycle: 'monthly' | 'yearly' | null) => {
    if (newBillingCycle !== null) {
      setBillingCycle(newBillingCycle);
    }
  };
  
  // Handle plan selection
  const handleSelectPlan = (plan: string) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // In a real implementation, you would redirect to a checkout page
    console.log('Selected plan:', plan, 'with billing cycle:', billingCycle);
  };
  
  // Pricing features
  const pricingFeatures: PricingFeature[] = [
    {
      name: 'YouTube Video Processing',
      free: true,
      basic: true,
      premium: true
    },
    {
      name: 'Full Capsule',
      free: true,
      basic: true,
      premium: true
    },
    {
      name: 'Key Insights',
      free: true,
      basic: true,
      premium: true
    },
    {
      name: 'Mind Map',
      free: true,
      basic: true,
      premium: true
    },
    {
      name: 'AI Chat',
      free: true,
      basic: true,
      premium: true
    },
    {
      name: 'Study Notes',
      free: true,
      basic: true,
      premium: true
    },
    {
      name: 'Multiple Choice Questions',
      free: false,
      basic: false,
      premium: true,
      tooltip: 'Generate MCQs to test your knowledge'
    },
    {
      name: 'Flash Cards',
      free: false,
      basic: false,
      premium: true,
      tooltip: 'Create flash cards for quick revision'
    },
    {
      name: 'Terminology Simplifier',
      free: false,
      basic: true,
      premium: true,
      tooltip: 'Simplify complex terms and jargon'
    },
    {
      name: 'FAQs Generator',
      free: false,
      basic: true,
      premium: true,
      tooltip: 'Generate frequently asked questions and answers'
    },
    {
      name: 'Dogla-Meter (Fact Checker)',
      free: false,
      basic: true,
      premium: true,
      tooltip: 'Check for manipulation and bias in content'
    },
    {
      name: 'Multi-language Support',
      free: false,
      basic: true,
      premium: true,
      tooltip: 'Support for Hindi, Hinglish, Brazilian, etc.'
    },
    {
      name: 'Monthly Capsule Limit',
      free: true,
      basic: true,
      premium: true,
      tooltip: 'Free: 5/month, Basic: 50/month, Premium: Unlimited'
    },
    {
      name: 'Video Length Limit',
      free: true,
      basic: true,
      premium: true,
      tooltip: 'Free: 20 min, Basic: 60 min, Premium: 180 min'
    },
    {
      name: 'Priority Processing',
      free: false,
      basic: false,
      premium: true,
      tooltip: 'Your capsules are processed with higher priority'
    },
    {
      name: 'Save Capsules to Library',
      free: true,
      basic: true,
      premium: true,
      tooltip: 'Free: 10 saved capsules, Basic: 100 saved capsules, Premium: Unlimited'
    },
    {
      name: 'Download Capsules',
      free: false,
      basic: true,
      premium: true,
      tooltip: 'Download capsules as PDF, Markdown, etc.'
    },
    {
      name: 'Custom Video Upload',
      free: false,
      basic: false,
      premium: true,
      tooltip: 'Upload your own videos for processing'
    }
  ];
  
  // Pricing plans
  const pricingPlans: PricingPlan[] = [
    {
      name: 'Free',
      description: 'For casual users who want to try out the platform',
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        '5 capsules per month',
        'Videos up to 20 minutes',
        'Basic output formats',
        'Save up to 10 capsules',
        'English language only'
      ],
      buttonText: isAuthenticated && userSubscription === 'free' ? 'Current Plan' : 'Get Started',
      buttonVariant: 'outlined',
      buttonColor: 'inherit'
    },
    {
      name: 'Basic',
      description: 'For regular users who need more features',
      monthlyPrice: 15,
      yearlyPrice: 150,
      features: [
        '50 capsules per month',
        'Videos up to 60 minutes',
        'All output formats except MCQs and Flash Cards',
        'Multi-language support',
        'Save up to 100 capsules',
        'Download capsules'
      ],
      popular: true,
      buttonText: isAuthenticated && userSubscription === 'basic' ? 'Current Plan' : 'Upgrade to Basic',
      buttonVariant: 'contained',
      buttonColor: 'primary'
    },
    {
      name: 'Premium',
      description: 'For power users who want everything',
      monthlyPrice: 30,
      yearlyPrice: 300,
      features: [
        'Unlimited capsules',
        'Videos up to 3 hours',
        'All output formats',
        'Priority processing',
        'Unlimited saved capsules',
        'Custom video upload'
      ],
      buttonText: isAuthenticated && userSubscription === 'premium' ? 'Current Plan' : 'Upgrade to Premium',
      buttonVariant: 'contained',
      buttonColor: 'secondary'
    }
  ];
  
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Choose Your Plan
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Get the most out of SharkPanzer with our flexible pricing plans
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <ToggleButtonGroup
            value={billingCycle}
            exclusive
            onChange={handleBillingCycleChange}
            aria-label="billing cycle"
            sx={{ 
              bgcolor: 'background.paper',
              borderRadius: 4,
              p: 0.5
            }}
          >
            <ToggleButton value="monthly" aria-label="monthly billing">
              Monthly
            </ToggleButton>
            <ToggleButton value="yearly" aria-label="yearly billing">
              Yearly
              <Chip 
                label="Save 17%" 
                size="small" 
                color="secondary" 
                sx={{ ml: 1 }} 
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      
      {/* Pricing Plans */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        {pricingPlans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.name}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                position: 'relative',
                ...(plan.popular ? {
                  border: '2px solid',
                  borderColor: 'primary.main',
                  boxShadow: 4
                } : {})
              }}
            >
              {plan.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    px: 2,
                    py: 0.5,
                    borderRadius: 4,
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                >
                  MOST POPULAR
                </Box>
              )}
              
              <CardHeader
                title={plan.name}
                subheader={plan.description}
                titleTypographyProps={{ align: 'center', variant: 'h5' }}
                subheaderTypographyProps={{ align: 'center' }}
                sx={{ pb: 0 }}
              />
              
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography component="h2" variant="h3" color="text.primary">
                    ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {billingCycle === 'monthly' ? '/mo' : '/year'}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <List sx={{ flexGrow: 1 }}>
                  {plan.features.map((feature) => (
                    <ListItem key={feature} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
                
                <Button
                  fullWidth
                  variant={plan.buttonVariant}
                  color={plan.buttonColor}
                  onClick={() => handleSelectPlan(plan.name.toLowerCase())}
                  disabled={isAuthenticated && userSubscription === plan.name.toLowerCase()}
                  startIcon={<CreditCardIcon />}
                  sx={{ 
                    mt: 2,
                    py: 1.5,
                    borderRadius: 4
                  }}
                >
                  {plan.buttonText}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Feature Comparison */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          borderRadius: 4
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          Feature Comparison
        </Typography>
        
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 650, mt: 3 }}>
            <Grid container>
              {/* Header */}
              <Grid item xs={6}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ p: 2 }}>
                  Feature
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle1" fontWeight="bold" align="center" sx={{ p: 2 }}>
                  Free
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle1" fontWeight="bold" align="center" sx={{ p: 2 }}>
                  Basic
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle1" fontWeight="bold" align="center" sx={{ p: 2 }}>
                  Premium
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider />
              </Grid>
              
              {/* Features */}
              {pricingFeatures.map((feature, index) => (
                <React.Fragment key={feature.name}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                      <Typography variant="body1">
                        {feature.name}
                      </Typography>
                      {feature.tooltip && (
                        <Tooltip title={feature.tooltip}>
                          <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      {feature.free ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      {feature.basic ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      {feature.premium ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </Box>
                  </Grid>
                  
                  {index < pricingFeatures.length - 1 && (
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          </Box>
        </Box>
      </Paper>
      
      {/* FAQ */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          mt: 4,
          borderRadius: 4
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          Frequently Asked Questions
        </Typography>
        
        <Grid container spacing={4} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Can I change my plan later?
            </Typography>
            <Typography variant="body1" paragraph>
              Yes, you can upgrade or downgrade your plan at any time. If you upgrade, you'll be charged the prorated difference. If you downgrade, you'll receive credit towards your next billing cycle.
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              How does the monthly capsule limit work?
            </Typography>
            <Typography variant="body1" paragraph>
              The monthly limit resets on the first day of each billing cycle. Unused capsules do not roll over to the next month.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Can I cancel my subscription?
            </Typography>
            <Typography variant="body1" paragraph>
              Yes, you can cancel your subscription at any time. You'll continue to have access to your plan until the end of your current billing cycle.
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              Do you offer refunds?
            </Typography>
            <Typography variant="body1" paragraph>
              We offer a 3-day money-back guarantee for all paid plans, provided you have used less than 10% of your subscription credits. If you're not satisfied with our service, you can request a refund within 3 days of your purchase.
            </Typography>
          </Grid>
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" gutterBottom>
            Have more questions?
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            to="/contact"
            sx={{ mt: 1, borderRadius: 4 }}
          >
            Contact Us
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PricingPage;
